import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useDispatch, useSelector } from "react-redux";
import {
  FiDollarSign,
  FiShoppingBag,
  FiRefreshCw,
  FiSearch,
  FiFilter,
  FiChevronDown,
  FiEye,
  FiDownload,
  FiCalendar,
  FiUser,
  FiClock,
  FiCheckCircle,
  FiXCircle,
  FiAlertCircle,
  FiArrowUp,
  FiArrowDown,
  FiX,
  FiCheck,
  FiLoader,
} from "react-icons/fi";
import { Helmet } from "react-helmet-async";
import { fetchAdminTransactions } from "../../store/slices/adminSlice";
import { GlassCard } from "../../components/ui/GlassCard";
import { LoadingSkeleton } from "../../components/ui/LoadingSkeleton";
import { format } from "date-fns";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import { adminService } from "../../services/adminService";

export const AdminTransactionsPage = () => {
  const dispatch = useDispatch();
  const {
    adminTransactions,
    adminTransactionsTotals,
    adminTransactionsPagination,
    isLoading,
  } = useSelector((state) => state.admin);
  const [showFilters, setShowFilters] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [typeFilter, setTypeFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [dateRange, setDateRange] = useState({ start: "", end: "" });
  const [sortBy, setSortBy] = useState("newest");
  const [page, setPage] = useState(1);
  const [showTransactionModal, setShowTransactionModal] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [showApproveConfirm, setShowApproveConfirm] = useState(false);
  const [approveLoading, setApproveLoading] = useState(false);

  const loadTransactions = () => {
    dispatch(
      fetchAdminTransactions({
        page,
        limit: 100,
        searchTerm: searchTerm || undefined,
        type: typeFilter !== "all" ? typeFilter : undefined,
        status: statusFilter !== "all" ? statusFilter : undefined,
        startDate: dateRange.start || undefined,
        endDate: dateRange.end || undefined,
        sortBy,
      }),
    );
  };

  useEffect(() => {
    loadTransactions();
  }, [dispatch, page, searchTerm, typeFilter, statusFilter, dateRange.start, dateRange.end, sortBy]);

  useEffect(() => {
    if (page !== 1) {
      setPage(1);
    }
  }, [searchTerm, typeFilter, statusFilter, dateRange.start, dateRange.end, sortBy]);

  const handleApprovePending = async () => {
    if (!selectedTransaction) return;

    try {
      setApproveLoading(true);
      await adminService.approvePendingTransaction(selectedTransaction._id);

      // Refresh transactions
      dispatch(
        fetchAdminTransactions({
          page,
          limit: 100,
          searchTerm: searchTerm || undefined,
          type: typeFilter !== "all" ? typeFilter : undefined,
          status: statusFilter !== "all" ? statusFilter : undefined,
          startDate: dateRange.start || undefined,
          endDate: dateRange.end || undefined,
          sortBy,
        }),
      );

      // Close modals and show success
      setShowApproveConfirm(false);
      setShowTransactionModal(false);
      setSelectedTransaction(null);

      // Optional: Show success toast/notification
      alert("Transaction approved successfully!");
    } catch (error) {
      console.error("Approve error:", error);
      alert(error.response?.data?.message || "Failed to approve transaction");
    } finally {
      setApproveLoading(false);
    }
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case "deposit":
        return <FiArrowDown className="w-4 h-4 text-green-500" />;
      case "purchase":
        return <FiShoppingBag className="w-4 h-4 text-blue-500" />;
      case "refund":
        return <FiRefreshCw className="w-4 h-4 text-orange-500" />;
      default:
        return <FiDollarSign className="w-4 h-4 text-luxury-500" />;
    }
  };

  const getMetadataFields = (metadata) => {
    if (!metadata) return [];
    const raw = metadata.korapayData || metadata;
    const fields = [];

    if (typeof raw !== "object" || raw === null) {
      return metadata.korapayReference
        ? [{ label: "Korapay Reference", value: metadata.korapayReference }]
        : [];
    }

    if (metadata.korapayReference) {
      fields.push({
        label: "Korapay Reference",
        value: metadata.korapayReference,
      });
    }
    if (raw.checkoutUrl) {
      fields.push({
        label: "Checkout URL",
        value: raw.checkoutUrl,
        link: true,
      });
    }
    if (raw.reference) {
      fields.push({ label: "Gateway Reference", value: raw.reference });
    }
    if (raw.status) {
      fields.push({ label: "Gateway Status", value: raw.status });
    }
    if (raw.description) {
      fields.push({ label: "Description", value: raw.description });
    }
    if (raw.amount !== undefined) {
      fields.push({
        label: "Amount",
        value: `₦${Number(raw.amount).toLocaleString()}`,
      });
    }
    if (raw.amount_paid !== undefined) {
      fields.push({
        label: "Amount Paid",
        value: `₦${Number(raw.amount_paid).toLocaleString()}`,
      });
    }
    if (raw.fee !== undefined) {
      fields.push({
        label: "Fee",
        value: `₦${Number(raw.fee).toLocaleString()}`,
      });
    }
    if (raw.currency) {
      fields.push({ label: "Currency", value: raw.currency });
    }
    if (raw.customer?.name) {
      fields.push({ label: "Customer Name", value: raw.customer.name });
    }
    if (raw.customer?.email) {
      fields.push({ label: "Customer Email", value: raw.customer.email });
    }
    if (raw.card) {
      if (raw.card.card_type) {
        fields.push({ label: "Card Type", value: raw.card.card_type });
      }
      if (raw.card.first_six || raw.card.last_four) {
        fields.push({
          label: "Card Number",
          value: `${raw.card.first_six || "••••••"} •••• ${raw.card.last_four || "••••"}`,
        });
      }
      if (raw.card.expiry) {
        fields.push({ label: "Card Expiry", value: raw.card.expiry });
      }
    }

    return fields;
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case "success":
        return (
          <span className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-xs font-medium">
            Success
          </span>
        );
      case "pending":
        return (
          <span className="px-3 py-1 bg-yellow-100 text-yellow-700 rounded-full text-xs font-medium">
            Pending
          </span>
        );
      case "failed":
        return (
          <span className="px-3 py-1 bg-red-100 text-red-700 rounded-full text-xs font-medium">
            Failed
          </span>
        );
      default:
        return (
          <span className="px-3 py-1 bg-luxury-100 text-luxury-700 rounded-full text-xs font-medium">
            {status}
          </span>
        );
    }
  };

  const calculateTotals = () => {
    return {
      totalDeposits: adminTransactionsTotals?.totalDeposits || 0,
      totalPurchases: adminTransactionsTotals?.totalPurchases || 0,
      totalRefunds: adminTransactionsTotals?.totalRefunds || 0,
      pendingTransactions: adminTransactionsTotals?.pendingTransactions || 0,
      successfulTransactions: adminTransactionsTotals?.successfulTransactions || 0,
      failedTransactions: adminTransactionsTotals?.failedTransactions || 0,
    };
  };

  const exportToExcel = () => {
    const data = adminTransactions.map((t) => ({
      Reference: t.reference,
      User: t.user?.username || "N/A",
      Email: t.user?.email || "N/A",
      Type: t.type,
      Amount: `₦${t.amount?.toLocaleString()}`,
      Status: t.status,
      "Payment Method": t.paymentMethod || "N/A",
      Date: format(new Date(t.createdAt), "MMM dd, yyyy HH:mm"),
      Account: t.account?.title || "N/A",
    }));

    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Transactions");
    const excelBuffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    const blob = new Blob([excelBuffer], { type: "application/octet-stream" });
    saveAs(blob, `transactions_${format(new Date(), "yyyy-MM-dd")}.xlsx`);
  };

  const totals = calculateTotals();
  const pagination = adminTransactionsPagination;

  const fadeInUp = {
    initial: { opacity: 0, y: 20 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.5 },
  };

  const staggerContainer = {
    animate: {
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  return (
    <>
      <Helmet>
        <title>Manage Transactions | Admin | SocialMarket</title>
      </Helmet>

      <div className="min-h-screen py-12 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-luxury-50 via-white to-luxury-100">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <motion.div
            variants={fadeInUp}
            initial="initial"
            animate="animate"
            className="mb-8"
          >
            <h1 className="text-3xl font-display font-bold text-luxury-900 mb-2">
              Transaction <span className="gradient-text">Management</span>
            </h1>
            <p className="text-luxury-600">
              Monitor and manage all platform transactions
            </p>
          </motion.div>

          {/* Stats Cards */}
          <motion.div
            variants={staggerContainer}
            initial="initial"
            animate="animate"
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8"
          >
            <motion.div variants={fadeInUp}>
              <GlassCard className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
                    <FiArrowDown className="w-6 h-6 text-green-600" />
                  </div>
                </div>
                <h3 className="text-2xl font-bold text-luxury-900 mb-1">
                  ₦{totals.totalDeposits.toLocaleString()}
                </h3>
                <p className="text-sm text-luxury-500">Total Deposits</p>
              </GlassCard>
            </motion.div>

            <motion.div variants={fadeInUp}>
              <GlassCard className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                    <FiShoppingBag className="w-6 h-6 text-blue-600" />
                  </div>
                </div>
                <h3 className="text-2xl font-bold text-luxury-900 mb-1">
                  ₦{totals.totalPurchases.toLocaleString()}
                </h3>
                <p className="text-sm text-luxury-500">Total Purchases</p>
              </GlassCard>
            </motion.div>

            <motion.div variants={fadeInUp}>
              <GlassCard className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="w-12 h-12 bg-orange-100 rounded-xl flex items-center justify-center">
                    <FiRefreshCw className="w-6 h-6 text-orange-600" />
                  </div>
                </div>
                <h3 className="text-2xl font-bold text-luxury-900 mb-1">
                  ₦{totals.totalRefunds.toLocaleString()}
                </h3>
                <p className="text-sm text-luxury-500">Total Refunds</p>
              </GlassCard>
            </motion.div>

            <motion.div variants={fadeInUp}>
              <GlassCard className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="w-12 h-12 bg-yellow-100 rounded-xl flex items-center justify-center">
                    <FiClock className="w-6 h-6 text-yellow-600" />
                  </div>
                </div>
                <h3 className="text-2xl font-bold text-luxury-900 mb-1">
                  {totals.pendingTransactions}
                </h3>
                <p className="text-sm text-luxury-500">Pending</p>
              </GlassCard>
            </motion.div>
          </motion.div>

          {/* Filters Bar */}
          <motion.div
            variants={fadeInUp}
            initial="initial"
            animate="animate"
            className="glass rounded-2xl p-4 mb-8"
          >
            <div className="flex flex-col lg:flex-row gap-4">
              {/* Search */}
              <div className="flex-1 relative">
                <FiSearch className="absolute left-4 top-1/2 transform -translate-y-1/2 text-luxury-400" />
                <input
                  type="text"
                  placeholder="Search by reference, user, or email..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="input-luxury pl-12 w-full"
                />
              </div>

              {/* Filter Toggle */}
              <button
                onClick={() => setShowFilters(!showFilters)}
                className={`px-6 py-3 rounded-xl flex items-center space-x-2 transition-all duration-300 ${
                  showFilters
                    ? "bg-primary-600 text-white"
                    : "glass hover:bg-primary-50 text-luxury-700"
                }`}
              >
                <FiFilter className="w-5 h-5" />
                <span>Filters</span>
                <FiChevronDown
                  className={`w-4 h-4 transition-transform duration-300 ${
                    showFilters ? "rotate-180" : ""
                  }`}
                />
              </button>

              {/* Export Button */}
              <button
                onClick={exportToExcel}
                className="px-6 py-3 glass rounded-xl flex items-center space-x-2 hover:bg-primary-50 transition-colors"
              >
                <FiDownload className="w-5 h-5" />
                <span>Export</span>
              </button>
            </div>

            {/* Filter Panel */}
            <AnimatePresence>
              {showFilters && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: "auto" }}
                  exit={{ opacity: 0, height: 0 }}
                  transition={{ duration: 0.3 }}
                  className="overflow-hidden"
                >
                  <div className="pt-6 mt-4 border-t border-luxury-200">
                    <div className="grid md:grid-cols-4 gap-4">
                      {/* Date Range */}
                      <div>
                        <label className="block text-sm font-medium text-luxury-700 mb-2">
                          From
                        </label>
                        <input
                          type="date"
                          value={dateRange.start}
                          onChange={(e) =>
                            setDateRange((prev) => ({
                              ...prev,
                              start: e.target.value,
                            }))
                          }
                          className="input-luxury"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-luxury-700 mb-2">
                          To
                        </label>
                        <input
                          type="date"
                          value={dateRange.end}
                          onChange={(e) =>
                            setDateRange((prev) => ({
                              ...prev,
                              end: e.target.value,
                            }))
                          }
                          className="input-luxury"
                        />
                      </div>

                      {/* Type Filter */}
                      <div>
                        <label className="block text-sm font-medium text-luxury-700 mb-2">
                          Type
                        </label>
                        <select
                          value={typeFilter}
                          onChange={(e) => setTypeFilter(e.target.value)}
                          className="input-luxury"
                        >
                          <option value="all">All Types</option>
                          <option value="deposit">Deposit</option>
                          <option value="purchase">Purchase</option>
                          <option value="refund">Refund</option>
                        </select>
                      </div>

                      {/* Status Filter */}
                      <div>
                        <label className="block text-sm font-medium text-luxury-700 mb-2">
                          Status
                        </label>
                        <select
                          value={statusFilter}
                          onChange={(e) => setStatusFilter(e.target.value)}
                          className="input-luxury"
                        >
                          <option value="all">All Status</option>
                          <option value="success">Success</option>
                          <option value="pending">Pending</option>
                          <option value="failed">Failed</option>
                        </select>
                      </div>

                      {/* Sort By */}
                      <div>
                        <label className="block text-sm font-medium text-luxury-700 mb-2">
                          Sort By
                        </label>
                        <select
                          value={sortBy}
                          onChange={(e) => setSortBy(e.target.value)}
                          className="input-luxury"
                        >
                          <option value="newest">Newest First</option>
                          <option value="oldest">Oldest First</option>
                          <option value="amount_high">
                            Amount: High to Low
                          </option>
                          <option value="amount_low">
                            Amount: Low to High
                          </option>
                        </select>
                      </div>
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>

          {/* Transactions Table */}
          {isLoading ? (
            <LoadingSkeleton count={5} />
          ) : (
            <motion.div
              variants={staggerContainer}
              initial="initial"
              animate="animate"
              className="glass rounded-2xl overflow-hidden"
            >
              <div className="p-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <p className="text-sm text-luxury-500">
                    Showing page {pagination.page} of {pagination.totalPages}
                  </p>
                  <p className="text-sm text-luxury-500">
                    Total matched transactions: {pagination.totalCount}
                  </p>
                </div>
                <div className="flex flex-wrap gap-2 text-sm text-luxury-500">
                  <span>Type: {typeFilter}</span>
                  <span>Status: {statusFilter}</span>
                  <span>Sort: {sortBy}</span>
                </div>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-luxury-100">
                    <tr className="text-left text-sm text-luxury-600">
                      <th className="px-6 py-4">Reference</th>
                      <th className="px-6 py-4">User</th>
                      <th className="px-6 py-4">Type</th>
                      <th className="px-6 py-4">Amount</th>
                      <th className="px-6 py-4">Status</th>
                      <th className="px-6 py-4">Payment Method</th>
                      <th className="px-6 py-4">Date</th>
                      <th className="px-6 py-4">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {adminTransactions.map((transaction) => (
                      <motion.tr
                        key={transaction._id}
                        variants={fadeInUp}
                        className="border-t border-luxury-200 hover:bg-luxury-50 transition-colors"
                      >
                        <td className="px-6 py-4">
                          <span className="text-sm font-mono text-luxury-900">
                            {transaction.reference}
                          </span>
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex items-center space-x-2">
                            <div className="w-8 h-8 bg-luxury-200 rounded-full flex items-center justify-center">
                              <FiUser className="w-4 h-4 text-luxury-500" />
                            </div>
                            <div>
                              <p className="text-sm font-medium text-luxury-900">
                                {transaction.user?.username || "N/A"}
                              </p>
                              <p className="text-xs text-luxury-500">
                                {transaction.user?.email || "N/A"}
                              </p>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex items-center space-x-2">
                            {getTypeIcon(transaction.type)}
                            <span className="text-sm capitalize text-luxury-600">
                              {transaction.type}
                            </span>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <span className="text-sm font-semibold gradient-text">
                            ₦{transaction.amount?.toLocaleString()}
                          </span>
                        </td>
                        <td className="px-6 py-4">
                          {getStatusBadge(transaction.status)}
                        </td>
                        <td className="px-6 py-4">
                          <span className="text-sm text-luxury-600 capitalize">
                            {transaction.paymentMethod || "N/A"}
                          </span>
                        </td>
                        <td className="px-6 py-4">
                          <span className="text-sm text-luxury-500">
                            {format(
                              new Date(transaction.createdAt),
                              "MMM dd, yyyy",
                            )}
                          </span>
                        </td>
                        <td className="px-6 py-4">
                          <button
                            onClick={() => {
                              setSelectedTransaction(transaction);
                              setShowTransactionModal(true);
                            }}
                            className="p-2 glass rounded-lg hover:bg-primary-50 transition-colors"
                            title="View Details"
                          >
                            <FiEye className="w-4 h-4" />
                          </button>
                        </td>
                      </motion.tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {pagination.totalPages > 1 && (
                <div className="flex flex-wrap items-center justify-between gap-3 border-t border-luxury-200 bg-luxury-50 p-4">
                  <p className="text-sm text-luxury-500">
                    Page {pagination.page} of {pagination.totalPages} · {pagination.totalCount} total
                  </p>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      disabled={pagination.page === 1}
                      onClick={() => setPage(Math.max(1, pagination.page - 1))}
                      className="rounded-xl border border-luxury-200 bg-white px-4 py-2 text-sm text-luxury-700 hover:bg-luxury-100 disabled:opacity-50"
                    >
                      Previous
                    </button>
                    <button
                      type="button"
                      disabled={pagination.page === pagination.totalPages}
                      onClick={() => setPage(Math.min(pagination.totalPages, pagination.page + 1))}
                      className="rounded-xl border border-luxury-200 bg-white px-4 py-2 text-sm text-luxury-700 hover:bg-luxury-100 disabled:opacity-50"
                    >
                      Next
                    </button>
                  </div>
                </div>
              )}
            </motion.div>
          )}

          {/* Transaction Details Modal */}
          <AnimatePresence>
            {showTransactionModal && selectedTransaction && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4"
                onClick={() => setShowTransactionModal(false)}
              >
                <motion.div
                  initial={{ scale: 0.9, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  exit={{ scale: 0.9, opacity: 0 }}
                  className="glass max-w-2xl w-full max-h-[90vh] overflow-y-auto rounded-2xl p-6"
                  onClick={(e) => e.stopPropagation()}
                >
                  <div className="flex items-center justify-between mb-6">
                    <h2 className="text-xl font-semibold text-luxury-900">
                      Transaction Details
                    </h2>
                    <div className="flex items-center gap-3">
                      {getStatusBadge(selectedTransaction.status)}
                      <button
                        onClick={() => setShowTransactionModal(false)}
                        className="text-luxury-900 hover:text-luxury-600 transition-colors"
                      >
                        <FiX className="w-6 h-6" />
                      </button>
                    </div>
                  </div>

                  <div className="space-y-4">
                    {/* Reference */}
                    <div className="p-4 bg-luxury-100 rounded-xl">
                      <p className="text-sm text-luxury-900 mb-1">Reference</p>
                      <p className="text-lg font-mono text-luxury-900 truncate">
                        {selectedTransaction.reference}
                      </p>
                    </div>

                    {/* User Info */}
                    <div className="flex items-center space-x-4 p-4 bg-luxury-100 rounded-xl">
                      <div className="w-12 h-12 bg-gradient-to-r from-primary-500 to-secondary-500 rounded-xl flex items-center justify-center">
                        <FiUser className="w-6 h-6 text-white" />
                      </div>
                      <div>
                        <p className="font-semibold text-luxury-900">
                          {selectedTransaction.user?.username}
                        </p>
                        <p className="text-sm text-luxury-700">
                          {selectedTransaction.user?.email}
                        </p>
                      </div>
                    </div>

                    {/* Transaction Details Grid */}
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <p className="text-sm text-luxury-900 mb-1">Type</p>
                        <div className="flex items-center space-x-2">
                          {getTypeIcon(selectedTransaction.type)}
                          <span className="font-medium text-luxury-900 capitalize">
                            {selectedTransaction.type}
                          </span>
                        </div>
                      </div>
                      <div>
                        <p className="text-sm text-luxury-900 mb-1">Amount</p>
                        <p className="text-xl font-bold gradient-text">
                          ₦{selectedTransaction.amount?.toLocaleString()}
                        </p>
                      </div>
                      <div>
                        <p className="text-sm text-luxury-900 mb-1">
                          Payment Method
                        </p>
                        <p className="font-medium text-luxury-900 capitalize">
                          {selectedTransaction.paymentMethod || "Wallet"}
                        </p>
                      </div>
                      <div>
                        <p className="text-sm text-luxury-900 mb-1">Date</p>
                        <p className="font-medium text-luxury-900">
                          {format(
                            new Date(selectedTransaction.createdAt),
                            "MMMM dd, yyyy",
                          )}
                        </p>
                        <p className="text-sm text-luxury-900">
                          {format(
                            new Date(selectedTransaction.createdAt),
                            "h:mm:ss a",
                          )}
                        </p>
                      </div>
                    </div>

                    {/* Account Details (if purchase) */}
                    {selectedTransaction.account && (
                      <div className="pt-4 border-t border-luxury-200">
                        <h3 className="font-semibold text-luxury-900 mb-3">
                          Account Details
                        </h3>
                        <div className="p-4 bg-luxury-100 rounded-xl">
                          <p className="font-medium text-luxury-900">
                            {selectedTransaction.account.title}
                          </p>
                          <p className="text-sm text-luxury-500">
                            {selectedTransaction.account.socialMediaType}
                          </p>
                        </div>
                      </div>
                    )}

                    {selectedTransaction.virtualNumberPurchase && (
                      <div className="pt-4 border-t border-luxury-200">
                        <h3 className="font-semibold text-luxury-900 mb-3">
                          Virtual Number Details
                        </h3>
                        <div className="grid grid-cols-1 gap-3">
                          <div className="p-3 bg-luxury-100 rounded-lg flex justify-between text-sm text-luxury-900">
                            <span>Service</span>
                            <span>
                              {
                                selectedTransaction.virtualNumberPurchase
                                  .serviceName
                              }
                            </span>
                          </div>
                          <div className="p-3 bg-luxury-100 rounded-lg flex justify-between text-sm text-luxury-900">
                            <span>Country</span>
                            <span>
                              {
                                selectedTransaction.virtualNumberPurchase
                                  .countryName
                              }
                            </span>
                          </div>
                          <div className="p-3 bg-luxury-100 rounded-lg flex justify-between text-sm text-luxury-900">
                            <span>Phone Number</span>
                            <span>
                              {
                                selectedTransaction.virtualNumberPurchase
                                  .phoneNumber
                              }
                            </span>
                          </div>
                          <div className="p-3 bg-luxury-100 rounded-lg flex justify-between text-sm text-luxury-900">
                            <span>Status</span>
                            <span>
                              {selectedTransaction.virtualNumberPurchase.status}
                            </span>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Metadata (if available) */}
                    {selectedTransaction.metadata && (
                      <div className="pt-4 border-t border-luxury-200">
                        <h3 className="font-semibold text-luxury-900 mb-3">
                          Additional Information
                        </h3>
                        <div className="space-y-2">
                          {selectedTransaction.metadata.serviceName && (
                            <div className="p-3 bg-luxury-100 rounded-lg flex justify-between items-start">
                              <span className="text-sm font-medium text-luxury-600">
                                Service
                              </span>
                              <span className="text-sm text-luxury-900 font-medium text-right">
                                {selectedTransaction.metadata.serviceName}
                              </span>
                            </div>
                          )}
                          {selectedTransaction.metadata.country && (
                            <div className="p-3 bg-luxury-100 rounded-lg flex justify-between items-start">
                              <span className="text-sm font-medium text-luxury-600">
                                Country
                              </span>
                              <span className="text-sm text-luxury-900 font-medium text-right">
                                {selectedTransaction.metadata.country}
                              </span>
                            </div>
                          )}
                          {selectedTransaction.metadata.phoneNumber && (
                            <div className="p-3 bg-luxury-100 rounded-lg flex justify-between items-start">
                              <span className="text-sm font-medium text-luxury-600">
                                Phone Number
                              </span>
                              <span className="text-sm text-luxury-900 font-medium text-right">
                                {selectedTransaction.metadata.phoneNumber}
                              </span>
                            </div>
                          )}
                          {selectedTransaction.metadata.codeReceived !==
                            undefined && (
                            <div className="p-3 bg-luxury-100 rounded-lg flex justify-between items-start">
                              <span className="text-sm font-medium text-luxury-600">
                                Code Received
                              </span>
                              <span className="text-sm text-luxury-900 font-medium text-right">
                                {selectedTransaction.metadata.codeReceived
                                  ? "Yes"
                                  : "No"}
                              </span>
                            </div>
                          )}
                          {selectedTransaction.metadata.smsCode && (
                            <div className="p-3 bg-luxury-100 rounded-lg flex justify-between items-start">
                              <span className="text-sm font-medium text-luxury-600">
                                SMS Code
                              </span>
                              <span className="text-sm text-luxury-900 font-medium text-right break-all">
                                {selectedTransaction.metadata.smsCode}
                              </span>
                            </div>
                          )}
                          {selectedTransaction.metadata.smsText && (
                            <div className="p-3 bg-luxury-100 rounded-lg flex justify-between items-start">
                              <span className="text-sm font-medium text-luxury-600">
                                SMS Message
                              </span>
                              <span className="text-sm text-luxury-900 font-medium text-right break-all">
                                {selectedTransaction.metadata.smsText}
                              </span>
                            </div>
                          )}
                          {selectedTransaction.metadata.activationStartTime && (
                            <div className="p-3 bg-luxury-100 rounded-lg flex justify-between items-start">
                              <span className="text-sm font-medium text-luxury-600">
                                Activation Starts
                              </span>
                              <span className="text-sm text-luxury-900 font-medium text-right">
                                {format(
                                  new Date(
                                    selectedTransaction.metadata
                                      .activationStartTime,
                                  ),
                                  "MMM dd, yyyy h:mm a",
                                )}
                              </span>
                            </div>
                          )}
                          {selectedTransaction.metadata.activationEndTime && (
                            <div className="p-3 bg-luxury-100 rounded-lg flex justify-between items-start">
                              <span className="text-sm font-medium text-luxury-600">
                                Activation Ends
                              </span>
                              <span className="text-sm text-luxury-900 font-medium text-right">
                                {format(
                                  new Date(
                                    selectedTransaction.metadata
                                      .activationEndTime,
                                  ),
                                  "MMM dd, yyyy h:mm a",
                                )}
                              </span>
                            </div>
                          )}
                          {getMetadataFields(selectedTransaction.metadata).map(
                            (field) => (
                              <div
                                key={field.label}
                                className="p-3 bg-luxury-100 rounded-lg flex justify-between items-start"
                              >
                                <span className="text-sm font-medium text-luxury-600">
                                  {field.label}
                                </span>
                                {field.link ? (
                                  <a
                                    href={field.value}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="text-sm text-blue-600 font-medium text-right hover:underline"
                                  >
                                    {field.value}
                                  </a>
                                ) : (
                                  <span className="text-sm text-luxury-900 font-medium text-right">
                                    {field.value}
                                  </span>
                                )}
                              </div>
                            ),
                          )}
                          {selectedTransaction.metadata.expirationMinutes !==
                            undefined && (
                            <div className="p-3 bg-luxury-100 rounded-lg flex justify-between items-start">
                              <span className="text-sm font-medium text-luxury-600">
                                TTL
                              </span>
                              <span className="text-sm text-luxury-900 font-medium text-right">
                                {selectedTransaction.metadata.expirationMinutes}{" "}
                                mins
                              </span>
                            </div>
                          )}
                          {selectedTransaction.metadata.reason && (
                            <div className="p-3 bg-luxury-100 rounded-lg flex justify-between items-start">
                              <span className="text-sm font-medium text-luxury-600">
                                Reason
                              </span>
                              <span className="text-sm text-luxury-900 font-medium text-right capitalize">
                                {selectedTransaction.metadata.reason.replace(
                                  "_",
                                  " ",
                                )}
                              </span>
                            </div>
                          )}
                          {selectedTransaction.metadata.celebUpdateTitle && (
                            <div className="p-3 bg-luxury-100 rounded-lg flex justify-between items-start">
                              <span className="text-sm font-medium text-luxury-600">
                                Update
                              </span>
                              <span className="text-sm text-luxury-900 font-medium text-right">
                                {selectedTransaction.metadata
                                  .celebUpdateTitle || "N/A"}
                              </span>
                            </div>
                          )}
                          {selectedTransaction.metadata.celebUpdatePrice && (
                            <div className="p-3 bg-luxury-100 rounded-lg flex justify-between items-start">
                              <span className="text-sm font-medium text-luxury-600">
                                Update Price
                              </span>
                              <span className="text-sm text-luxury-900 font-medium text-right">
                                ₦
                                {selectedTransaction.metadata.celebUpdatePrice.toLocaleString()}
                              </span>
                            </div>
                          )}
                          {selectedTransaction.metadata.accountTitle && (
                            <div className="p-3 bg-luxury-100 rounded-lg flex justify-between items-start">
                              <span className="text-sm font-medium text-luxury-600">
                                Account
                              </span>
                              <span className="text-sm text-luxury-900 font-medium text-right">
                                {selectedTransaction.metadata.accountTitle}
                              </span>
                            </div>
                          )}
                          {selectedTransaction.metadata.balanceBefore !==
                            undefined && (
                            <div className="p-3 bg-luxury-100 rounded-lg flex justify-between items-start">
                              <span className="text-sm font-medium text-luxury-600">
                                Balance Before
                              </span>
                              <span className="text-sm text-luxury-900 font-medium text-right">
                                ₦
                                {selectedTransaction.metadata.balanceBefore.toLocaleString()}
                              </span>
                            </div>
                          )}
                          {selectedTransaction.metadata.balanceAfter !==
                            undefined && (
                            <div className="p-3 bg-luxury-100 rounded-lg flex justify-between items-start">
                              <span className="text-sm font-medium text-luxury-600">
                                Balance After
                              </span>
                              <span className="text-sm text-luxury-900 font-medium text-right">
                                ₦
                                {selectedTransaction.metadata.balanceAfter.toLocaleString()}
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="flex justify-end gap-3 mt-6">
                    {selectedTransaction.status === "pending" &&
                      selectedTransaction.type === "deposit" && (
                        <button
                          onClick={() => setShowApproveConfirm(true)}
                          disabled={approveLoading}
                          className="px-6 py-2 bg-green-600 text-white rounded-xl hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                        >
                          {approveLoading ? (
                            <>
                              <FiLoader className="w-4 h-4 animate-spin" />
                              Approving...
                            </>
                          ) : (
                            <>
                              <FiCheck className="w-4 h-4" />
                              Approve
                            </>
                          )}
                        </button>
                      )}
                    <button
                      onClick={() => setShowTransactionModal(false)}
                      className="px-6 py-2 bg-primary-600 text-white rounded-xl hover:bg-primary-700 transition-colors"
                    >
                      Close
                    </button>
                  </div>
                </motion.div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Approve Confirmation Modal */}
          <AnimatePresence>
            {showApproveConfirm && selectedTransaction && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4"
                onClick={() => setShowApproveConfirm(false)}
              >
                <motion.div
                  initial={{ scale: 0.9, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  exit={{ scale: 0.9, opacity: 0 }}
                  className="glass max-w-md w-full rounded-2xl p-6"
                  onClick={(e) => e.stopPropagation()}
                >
                  <div className="flex items-center justify-center w-12 h-12 rounded-full bg-green-100 mx-auto mb-4">
                    <FiAlertCircle className="w-6 h-6 text-green-600" />
                  </div>

                  <h3 className="text-xl font-semibold text-luxury-900 text-center mb-2">
                    Approve Transaction
                  </h3>

                  <p className="text-luxury-600 text-center mb-6">
                    Are you sure you want to approve this pending deposit of{" "}
                    <span className="font-bold gradient-text">
                      ₦{selectedTransaction.amount?.toLocaleString()}
                    </span>{" "}
                    for{" "}
                    <span className="font-bold">
                      {selectedTransaction.user?.username}
                    </span>
                    ?
                  </p>

                  <div className="bg-luxury-100 rounded-lg p-4 mb-6">
                    <p className="text-sm text-luxury-600 mb-1">Reference</p>
                    <p className="font-mono text-sm text-luxury-900">
                      {selectedTransaction.reference}
                    </p>
                  </div>

                  <div className="flex gap-3">
                    <button
                      onClick={() => setShowApproveConfirm(false)}
                      disabled={approveLoading}
                      className="flex-1 px-4 py-2 glass rounded-xl hover:bg-luxury-50 transition-colors disabled:opacity-50 text-luxury-900"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleApprovePending}
                      disabled={approveLoading}
                      className="flex-1 px-4 py-2 bg-green-600 text-white rounded-xl hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                    >
                      {approveLoading ? (
                        <>
                          <FiLoader className="w-4 h-4 animate-spin" />
                          Approving...
                        </>
                      ) : (
                        <>
                          <FiCheck className="w-4 h-4" />
                          Approve
                        </>
                      )}
                    </button>
                  </div>
                </motion.div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </>
  );
};
